// /components/NewWorkflow/shared/SchemaViewer.js

import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  Type,
  Hash,
  ToggleRight,
  Braces,
  Brackets,
  Link2,
  PlusCircle,
  ChevronRight,
} from 'lucide-react';

const cn = (...inputs) => twMerge(clsx(inputs));

const DATA_TYPES = {
  string: { label: 'String', icon: <Type size={14} /> },
  number: { label: 'Number', icon: <Hash size={14} /> },
  boolean: { label: 'Boolean', icon: <ToggleRight size={14} /> },
  object: { label: 'Object', icon: <Braces size={14} /> },
  array: { label: 'Array', icon: <Brackets size={14} /> },
};

const Field = ({
  field,
  path,
  isOutput,
  onFieldClick,
  onPassThroughClick,
  selectedPath,
  registerRef = () => {},
  mappings = {},
  isValidField = () => true,
  isInsideArray = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const dataType = (field.dataType || 'string').toLowerCase();
  const arrayItemType = (field.arrayItemType || 'string').toLowerCase();
  const info = DATA_TYPES[dataType] || DATA_TYPES.string;

  const isSelected = selectedPath === path;

  // const isClickable = !!onFieldClick;
  // const isDimmed = onFieldClick && !isValidField(field); // Check if field should be dimmed
  // ⭐ 2. Determine if the field is selectable based on the new flag and existing validation.
  const isClickableContext = !!onFieldClick;
  const isSelectable =
    isClickableContext && !isInsideArray && isValidField(field);

  const isMapped = useMemo(() => {
    if (isOutput) {
      return !!mappings[field.id];
    }
    return Object.values(mappings).some((p) => p.join('.') === path);
  }, [isOutput, mappings, field.id, path]);

  const mappedFromPath = isOutput ? (mappings[field.id] || []).join('.') : null;
  const isNestable =
    (dataType === 'object' ||
      (dataType === 'array' && arrayItemType === 'object')) &&
    (field.children || []).length > 0;

  const handlePassThrough = (e) => {
    e.stopPropagation();
    if (onPassThroughClick) {
      onPassThroughClick(field, path);
    }
  };

  return (
    <div className="relative pl-4 before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-slate-700">
      <div className="relative">
        {/* eslint-disable-next-line */}
        <div
          // onClick={() => isClickable && !isDimmed && onFieldClick(path, field)}
          onClick={() => isSelectable && onFieldClick(path, field)}
          className={cn(
            'group flex items-center justify-between rounded-md border py-1.5 pl-2 pr-1 transition-all duration-150',
            'before:absolute before:left-[-17px] before:top-1/2 before:h-px before:w-4 before:bg-slate-700',
            // isClickable && "cursor-pointer",
            isSelected
              ? 'border-indigo-500 bg-indigo-600/30'
              : 'border-transparent bg-slate-900/50 hover:bg-slate-700/50',
            isMapped && 'border-teal-500/30 bg-teal-600/20',
            // isClickable && !isDimmed && "cursor-pointer",
            // isClickable && isDimmed && "opacity-40 cursor-not-allowed", // Dim invalid fields
            // ⭐ 3. Update styling logic to dim and disable non-selectable fields.
            isClickableContext &&
              isSelectable &&
              'cursor-pointer hover:bg-slate-700/50',
            isClickableContext &&
              !isSelectable &&
              'cursor-not-allowed opacity-50',
          )}
        >
          <div
            ref={(el) =>
              registerRef(
                `handle-${isOutput ? 'target' : 'source'}-${path}`,
                el,
              )
            }
            className="absolute left-0 top-1/2 z-10 h-2.5 w-2.5 -translate-x-[5px] rounded-full bg-slate-600 transition-colors group-hover:bg-slate-400"
          />

          <div className="flex min-w-0 flex-grow items-center gap-2">
            {isNestable ? (
              <ChevronRight
                size={16}
                className={cn(
                  'flex-shrink-0 cursor-pointer text-slate-500 transition-transform',
                  isExpanded && 'rotate-90',
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              />
            ) : (
              <div className="w-4 flex-shrink-0" />
            )}
            {isMapped && isOutput && (
              <Link2 size={14} className="flex-shrink-0 text-teal-400" />
            )}
            <span
              className="truncate font-mono text-sm text-white"
              title={field.fieldName}
            >
              {field.fieldName || <i className="text-slate-500">untitled</i>}
            </span>
            <span className="flex flex-shrink-0 items-center gap-1.5 text-xs text-slate-400">
              {info.icon} {info.label}
            </span>
          </div>

          <div className="flex flex-shrink-0 items-center">
            {/* --- THIS IS THE RESTORED PASS-THROUGH BUTTON --- */}
            {!isOutput && !isMapped && onPassThroughClick && (
              <button
                onClick={handlePassThrough}
                className="mr-2 text-slate-400 opacity-0 transition-all hover:text-indigo-400 group-hover:opacity-100"
                title={`Add "${field.fieldName}" to output`}
              >
                <PlusCircle size={16} />
              </button>
            )}
            <div
              ref={(el) =>
                registerRef(
                  `handle-${isOutput ? 'source' : 'target'}-${path}`,
                  el,
                )
              }
              className="relative right-[-13px] z-10 h-2.5 w-2.5 rounded-full bg-slate-600 transition-colors group-hover:bg-slate-400"
            />
          </div>
        </div>

        {isMapped && isOutput && mappedFromPath && (
          <div className="mt-1 truncate pl-8 font-mono text-xs text-indigo-300">
            <span className="text-slate-500">maps from → </span>
            {mappedFromPath}
          </div>
        )}
      </div>

      {isNestable && isExpanded && (
        <div className="mt-2 space-y-2">
          {field.children.map((child) => (
            <Field
              key={child.id}
              field={child}
              path={`${path}.${child.fieldName}`}
              isInsideArray={isInsideArray || dataType === 'array'}
              // ⭐ THE FIX: Ensure `isValidField` is passed down to child components
              {...{
                onFieldClick,
                onPassThroughClick,
                selectedPath,
                registerRef,
                isOutput,
                mappings,
                isValidField,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SchemaViewer = ({ rootFields, ...props }) => {
  if (!rootFields || rootFields.length === 0) {
    return (
      <div className="rounded-lg bg-slate-800 p-4 text-center text-sm text-slate-500">
        No Schema Available
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg bg-slate-800 p-2">
      {rootFields.map((rootField) => (
        <Field
          key={rootField.id}
          field={rootField}
          path={rootField.id}
          {...props}
        />
      ))}
    </div>
  );
};

export default SchemaViewer;
